import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { supabase, type Student, MASTER_OPERATOR_ID } from '@/lib/supabase';
import PortfolioView from '@/pages/dashboard/components/PortfolioView';

interface AcademyInfo {
  id: string;
  name: string;
  address: string | null;
}

export default function AcademyDetailPage() {
  const { profile, loading: authLoading, session } = useAuth();
  const { academyId } = useParams<{ academyId: string }>();
  const navigate = useNavigate();

  const [academy, setAcademy] = useState<AcademyInfo | null>(null);
  const [students, setStudents] = useState<Student[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const isMasterOperator = profile?.role === 'operator' && profile?.id === MASTER_OPERATOR_ID;

  const load = useCallback(async () => {
    if (!academyId) return;
    setLoading(true);
    setError('');
    try {
      const [academyRes, studentsRes] = await Promise.all([
        supabase.from('academies').select('id, name, address').eq('id', academyId).maybeSingle(),
        supabase
          .from('students')
          .select('*')
          .eq('academy_id', academyId)
          .order('created_at', { ascending: false }),
      ]);

      if (academyRes.error) throw academyRes.error;
      if (studentsRes.error) throw studentsRes.error;

      setAcademy((academyRes.data as AcademyInfo) || null);
      const list = (studentsRes.data as Student[]) || [];
      setStudents(list);
      setSelectedId((prev) =>
        prev && list.some((s) => s.id === prev) ? prev : list[0]?.id ?? null,
      );
    } catch (e) {
      setError('학원 정보를 불러오지 못했습니다.');
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [academyId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!authLoading && !session) {
      navigate('/login');
    }
  }, [authLoading, session, navigate]);

  if (authLoading) {
    return (
      <div className="min-h-screen bg-background-50 flex items-center justify-center">
        <i className="ri-loader-4-line animate-spin text-3xl text-primary-500"></i>
      </div>
    );
  }

  if (!session) {
    return null;
  }

  if (!isMasterOperator) {
    return (
      <div className="min-h-screen bg-background-50 flex items-center justify-center px-4">
        <div className="bg-white rounded-2xl border border-background-200 p-8 max-w-md w-full text-center">
          <div className="w-14 h-14 mx-auto flex items-center justify-center rounded-full bg-red-100 text-red-600 mb-4">
            <i className="ri-lock-line text-2xl"></i>
          </div>
          <h1 className="font-heading text-xl font-bold text-foreground-950 mb-2">
            접근 권한이 없습니다
          </h1>
          <p className="text-foreground-500 text-sm leading-relaxed mb-6">
            이 페이지는 운영자 황명철님만 볼 수 있습니다.
          </p>
          <button
            onClick={() => navigate('/dashboard')}
            className="whitespace-nowrap bg-primary-500 hover:bg-primary-600 text-white font-semibold px-6 py-2.5 rounded-md transition-colors cursor-pointer text-sm"
          >
            대시보드로 돌아가기
          </button>
        </div>
      </div>
    );
  }

  const selectedStudent = students.find((s) => s.id === selectedId) || null;

  return (
    <div className="min-h-screen bg-background-50">
      <header className="bg-white border-b border-background-200">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/dashboard')}
              className="whitespace-nowrap inline-flex items-center gap-1.5 text-sm text-foreground-600 hover:text-foreground-900 transition-colors cursor-pointer"
            >
              <i className="ri-arrow-left-line"></i>
              대시보드
            </button>
          </div>
          <div className="flex items-center gap-2">
            <span className="hidden sm:inline-flex items-center gap-1 px-3 py-1 rounded-full bg-accent-100 text-accent-800 text-xs font-semibold">
              <i className="ri-eye-line"></i>
              학원 보기
            </span>
            <span className="text-sm text-foreground-500">{profile?.full_name}</span>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-6 py-8">
        {error && (
          <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-4 py-3 mb-4">
            {error}
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <i className="ri-loader-4-line animate-spin text-3xl text-primary-500"></i>
          </div>
        ) : (
          <div>
            <div className="mb-6">
              <h1 className="font-heading text-2xl font-bold text-foreground-950">
                {academy?.name || '학원'}
              </h1>
              <p className="text-foreground-500 text-sm mt-1 flex items-center gap-2 flex-wrap">
                <span className="inline-flex items-center gap-1">
                  <i className="ri-map-pin-line"></i>
                  {academy?.address || '주소 미기입'}
                </span>
                <span>·</span>
                <span>
                  등록 학생 <strong className="text-foreground-900">{students.length}</strong>명
                </span>
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
              <div className="lg:col-span-2">
                <h2 className="font-heading text-lg font-bold text-foreground-950 mb-3">
                  학생 목록
                </h2>
                <div className="bg-white rounded-xl border border-background-200 divide-y divide-background-100">
                  {students.length === 0 ? (
                    <div className="p-8 text-center text-foreground-500 text-sm">
                      등록된 학생이 없습니다.
                    </div>
                  ) : (
                    students.map((student) => (
                      <div
                        key={student.id}
                        onClick={() => setSelectedId(student.id)}
                        className={`p-4 flex items-center gap-4 cursor-pointer transition-colors ${
                          selectedId === student.id ? 'bg-primary-50' : 'hover:bg-background-50'
                        }`}
                      >
                        <div className="w-10 h-10 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-700 font-semibold flex-shrink-0">
                          {student.name.charAt(0)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-semibold text-foreground-950 text-sm">
                            {student.name}
                          </div>
                          <div className="text-xs text-foreground-500 flex items-center gap-1.5 flex-wrap">
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-background-100 text-foreground-600">
                              {student.grade || '학년 미정'}
                            </span>
                            <span>{student.school || '학교 미기입'}</span>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>

              <div className="lg:col-span-3">
                {selectedStudent ? (
                  <PortfolioView student={selectedStudent} />
                ) : (
                  <div className="bg-white rounded-xl border border-background-200 p-10 text-center">
                    <div className="w-14 h-14 mx-auto flex items-center justify-center rounded-full bg-background-100 text-foreground-400 mb-3">
                      <i className="ri-user-line text-2xl"></i>
                    </div>
                    <div className="text-foreground-500 text-sm">
                      학생을 선택하면 포트폴리오를 볼 수 있습니다.
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}